import { Field, RichText, Text } from "@sitecore-jss/sitecore-jss-react";
import moment from "moment";
import React from "react";

import { dupInsuranceProductTypes, dupPaymentPeriod } from "../../../Enums/enumValues";
import { allMandatory } from "../../../helpers/allMandatory/allMandatory";
import { DefaultCoverage, DupInsuranceProductType, PaymentPeriod } from "../../../interfaces/dup";
import { ModelState } from "../../../interfaces/modelState";
import dateUtil from "../../../lib/dateUtil";
import {
  InsuredAmounts,
  PersonalDetailsModel,
  UpdateInsuredAmountPayload,
} from "../../../providers/dupFlow/DupFlow.interfaces";
import { Card } from "../../card/Card";
import { Fieldset } from "../../fieldset/Fieldset";
import Datepicker from "../../InputFields/DatePicker";
import HorizontalRadioInput from "../../InputFields/HorizontalRadioInput";
import NumberInput from "../../InputFields/NumberInput";
import { currencyThousandFormat } from "../../InputFields/sanitizer/currencyFormat";
import { roundValueOffset } from "../../InputFields/sanitizer/roundValueOffset";
import thousandsFormat from "../../InputFields/sanitizer/thousandsFormat";
import SliderInput from "../../InputFields/SliderInput";
import { BR208, BR209, V5 } from "../../InputFields/validator/validators";
import { RadioOption, VerticalRadioInput } from "../../InputFields/VerticalRadioInput";
import { calculateIsYougerThenAge } from "./calculateIsYoungerThenAge";

interface Props {
  insuredPersons: Partial<PersonalDetailsModel>[];
  defaultCoverage: Partial<DefaultCoverage>;
  fields: Record<string, Field<string>>;
  modelState: ModelState | null;
  setModelState: (updatedModelState: ModelState) => void;
  insuredAmounts: InsuredAmounts;
  hasCompletedInsureds: boolean;
  onUpdateInsuranceType: (insuranceType: DupInsuranceProductType) => void;
  onUpdateInsuredAmount: ({ insuredAmount, hasInsuredAmount }: UpdateInsuredAmountPayload) => void;
  onUpdateHasInsuredAmount: () => void;
  onUpdatePaymentPeriod: (paymentPeriod: PaymentPeriod) => void;
  onUpdateEffectiveDate: (date?: string) => void;
}

export const DupDefaultCoverage = ({
  insuredPersons,
  defaultCoverage,
  fields,
  hasCompletedInsureds,
  insuredAmounts,
  onUpdateInsuranceType,
  onUpdateInsuredAmount,
  onUpdateHasInsuredAmount,
  onUpdatePaymentPeriod,
  onUpdateEffectiveDate,
  modelState,
  setModelState,
}: Props) => {
  const step = 100;
  const validationPropertyName = "model.DefaultCoverage";
  const minDate = moment().add(1, "days").toDate();
  const maxDate = moment().add(3, "months").toDate();

  if (!defaultCoverage.effectiveDate) {
    const effectiveDate = dateUtil.tomorrow().format("DD-MM-YYYY");
    onUpdateEffectiveDate(effectiveDate);
  }

  const until65Disabled = calculateIsYougerThenAge(insuredPersons, defaultCoverage.effectiveDate, 55);
  const allIsDisabledExceptIsPurchaseMoney = calculateIsYougerThenAge(
    insuredPersons,
    defaultCoverage.effectiveDate,
    75
  );
  const paymentPeriods = dupPaymentPeriod() as RadioOption<PaymentPeriod>[];
  if (paymentPeriods.length) {
    paymentPeriods[0].disabled = until65Disabled;
  }

  const isServiceInsuranceChoosen = defaultCoverage.insuranceType === "ServicesProduct";

  const updateEffectiveDate = (date?: string) => {
    onUpdateEffectiveDate(date);
  };

  const handleUpdateInsuredAmount = (hasInsuredAmount: boolean) => (insuredAmount?: number) => {
    onUpdateInsuredAmount({ hasInsuredAmount, insuredAmount });
  };

  const isPurchaseMoneyUnavailable = insuredPersons.length > 2;

  const insuranceTypes = dupInsuranceProductTypes() as RadioOption<DupInsuranceProductType>[];

  if (insuranceTypes.length) {
    if (allIsDisabledExceptIsPurchaseMoney) {
      insuranceTypes[0].disabled = true;
      insuranceTypes[1].disabled = true;
    }
    if (isPurchaseMoneyUnavailable) {
      insuranceTypes[2].disabled = true;
    }
  }

  const isEffectiveFromDisabled = !allMandatory(
    hasCompletedInsureds,
    defaultCoverage.hasInsuredAmount,
    defaultCoverage.paymentPeriod || defaultCoverage.insuranceType === "PurchaseMoneyProduct"
  );

  const id = `dup-default-coverage`;
  const insuranceTypeHeadingId = `${id}-insurance-type-heading`;
  const insuranceAmountHeadingId = `${id}-insurance-amount-heading`;
  const paymentPeriodHeadingId = `${id}-payment-period-heading`;

  const showInsuredAmount = allMandatory(
    hasCompletedInsureds,
    defaultCoverage.insuranceType !== "PurchaseMoneyProduct"
  );

  const disableAmount = !allMandatory(hasCompletedInsureds, defaultCoverage.insuranceType);

  return (
    <>
      <Fieldset aria-labelledby={insuranceTypeHeadingId} disabled={!hasCompletedInsureds}>
        <Card>
          <h2 id={insuranceTypeHeadingId}>
            <Text field={fields.InsuranceTypeTitle} />
          </h2>
          <VerticalRadioInput
            fieldData={fields}
            radioRowClassName="form-item full-width radio"
            name="InsuranceType"
            fieldName="InsuranceType"
            selectedRadioItem={defaultCoverage.insuranceType}
            modelState={modelState}
            setModelState={setModelState}
            onChange={onUpdateInsuranceType}
            propertyName={`${validationPropertyName}.Insurancetype`}
            options={insuranceTypes}
            disabled={!hasCompletedInsureds}
          />
          {allIsDisabledExceptIsPurchaseMoney && (
            <div className="form-row">
              <div className="form-item">
                <span>
                  <RichText field={fields.WarningOnlyPurchaseMoneyAvailable} />
                </span>
              </div>
            </div>
          )}
          {isPurchaseMoneyUnavailable && (
            <div className="form-row">
              <div className="form-item">
                <span>
                  <RichText field={fields.WarningPurchaseMoneyNotAvailable} />
                </span>
              </div>
            </div>
          )}
        </Card>
      </Fieldset>
      <Fieldset aria-labelledby={insuranceAmountHeadingId} disabled={disableAmount}>
        <Card>
          <h2 id={insuranceAmountHeadingId}>
            <Text
              field={isServiceInsuranceChoosen ? fields.InsuranceServiceAmountTitle : fields.InsuranceMoneyAmountTitle}
            />
          </h2>

          <NumberInput
            fieldData={fields}
            name={isServiceInsuranceChoosen ? "InsuranceServiceAmount" : "InsuranceMoneyAmount"}
            fieldName={"InsuredAmount"}
            inputValue={defaultCoverage.insuredAmount}
            sanitize={thousandsFormat}
            validators={[V5, BR208(insuredAmounts.maxAmount), BR209(insuredAmounts.minAmount)]}
            onChange={handleUpdateInsuredAmount(true)}
            modelState={modelState}
            setModelState={setModelState}
            propertyName={`${validationPropertyName}.InsuredAmount`}
          />
          <SliderInput
            name="InsuredAmountSlider"
            fieldName="InsuredAmountSlider"
            fieldData={fields}
            inputValue={defaultCoverage.insuredAmount}
            onChange={handleUpdateInsuredAmount(false)}
            onChangeCommitted={onUpdateHasInsuredAmount}
            min={insuredAmounts.minAmount}
            max={insuredAmounts.maxAmount}
            step={step}
            dynamicMinLabel={currencyThousandFormat(insuredAmounts.minAmount)}
            dynamicMaxLabel={currencyThousandFormat(insuredAmounts.maxAmount)}
            defaultInputValue={insuredAmounts.minAmount}
            sanitize={[roundValueOffset(step, insuredAmounts.minAmount, insuredAmounts.maxAmount)]}
          />
        </Card>
      </Fieldset>

      {showInsuredAmount && (
        <Fieldset aria-labelledby={paymentPeriodHeadingId} disabled={!defaultCoverage.hasInsuredAmount}>
          <Card>
            <h2 id={paymentPeriodHeadingId}>
              <Text field={fields.InsurancePaymentPeriodTitle} />
            </h2>
            <HorizontalRadioInput
              disabled={!defaultCoverage.hasInsuredAmount}
              fieldData={fields}
              name="PaymentPeriod"
              fieldName="PaymentPeriod"
              selectedValue={defaultCoverage.paymentPeriod}
              onChange={onUpdatePaymentPeriod}
              modelState={modelState}
              setModelState={setModelState}
              propertyName={`${validationPropertyName}.PaymentPeriod`}
              options={paymentPeriods}
            />
            {until65Disabled && (
              <div className="form-row">
                <div className="form-item">
                  <span>
                    <RichText field={fields.WarningPaymentPeriod65NotPossible} />
                  </span>
                </div>
              </div>
            )}
          </Card>
        </Fieldset>
      )}
      <Fieldset disabled={isEffectiveFromDisabled}>
        <Card>
          <h2>
            <Text field={fields.InsuranceEffectiveDateTitle} />
          </h2>
          <Datepicker
            disabled={isEffectiveFromDisabled}
            fieldData={fields}
            name="EffectiveDate"
            fieldName="EffectiveDate"
            inputValue={defaultCoverage.effectiveDate}
            modelState={modelState}
            setModelState={setModelState}
            minDate={minDate}
            maxDate={maxDate}
            onChange={updateEffectiveDate}
            propertyName={`${validationPropertyName}.EffectiveDate`}
            required
          />
        </Card>
      </Fieldset>
    </>
  );
};
